import React, { useState, useEffect, useRef } from 'react';
import { Check, X } from 'lucide-react';
import { investmentProcess } from '../data/mockData';

const DifferentlySection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % investmentProcess.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-32 bg-white relative overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, black 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-20 transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">
            How Frontier does
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-600 to-black">
              things differently
            </span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-lg">
            Traditional investing is broken. You need a better way to manage your money.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Steps Navigation */}
          <div className={`space-y-4 transform transition-all duration-1000 delay-300 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
          }`}>
            {investmentProcess.map((step, index) => (
              <div
                key={step.id}
                onClick={() => setActiveStep(index)}
                className={`group cursor-pointer p-6 rounded-2xl border-2 transition-all duration-500 ${
                  activeStep === index
                    ? 'bg-black text-white border-black shadow-2xl scale-[1.02]'
                    : 'bg-white text-black border-gray-100 hover:border-gray-200 hover:shadow-lg'
                }`}
              >
                <div className="flex items-start gap-4">
                  <span className={`text-4xl font-bold transition-colors duration-500 ${
                    activeStep === index ? 'text-white/30' : 'text-gray-200'
                  }`}>
                    {step.step}
                  </span>
                  <div>
                    <h3 className={`text-xl font-bold mb-2 transition-colors duration-500 ${
                      activeStep === index ? 'text-white' : 'text-black'
                    }`}>
                      {step.title}
                    </h3>
                    <p className={`text-sm transition-colors duration-500 ${
                      activeStep === index ? 'text-white/70' : 'text-gray-500'
                    }`}>
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right: Comparison Card */}
          <div className={`transform transition-all duration-1000 delay-500 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
          }`}>
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 lg:p-10 border border-gray-100 shadow-xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-3 bg-black rounded-xl">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <span className="text-lg font-bold text-black">FRONTIER</span>
              </div>

              <div className="space-y-6">
                {/* Frontier Approach */}
                <div className="p-6 bg-emerald-50 rounded-2xl border border-emerald-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-1.5 bg-emerald-500 rounded-full">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-emerald-700 font-semibold">Frontier Approach</span>
                  </div>
                  <p className="text-gray-700 text-lg">
                    {investmentProcess[activeStep].frontierApproach}
                  </p>
                </div>

                {/* VS Divider */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="px-4 py-1 bg-gray-100 rounded-full text-gray-500 text-sm font-medium">
                    VS
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                {/* Traditional Approach */}
                <div className="p-6 bg-gray-50 rounded-2xl border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-1.5 bg-gray-300 rounded-full">
                      <X className="w-4 h-4 text-gray-600" />
                    </div>
                    <span className="text-gray-500 font-semibold">Traditional Wealth Firms</span>
                  </div>
                  <p className="text-gray-500">
                    {investmentProcess[activeStep].traditionalApproach}
                  </p>
                </div>
              </div>

              {/* Progress Indicator */}
              <div className="mt-8 flex items-center gap-2">
                {investmentProcess.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      idx === activeStep
                        ? 'w-12 bg-black'
                        : idx < activeStep
                        ? 'w-3 bg-black/50'
                        : 'w-3 bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DifferentlySection;
